import {
  Component,
  OnInit,
  ViewChildren,
  ViewChild,
  QueryList,
  AfterViewInit,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { CoreService } from '../../../services'
import { PopoverDirective } from 'ngx-bootstrap/popover'
import { FilterFormComponent } from '../../_common/filter-form/filter-form.component'
import { createSubmittedFilterState, SchemaUtil, withDateBoundary } from '../../../utils'
import { ProductDetailService } from '../../../product-detail.service'
import { BaseListing } from 'src/app/pages/_base-listing/base-listing'
import { TagService } from '../../../services/entity-services/tag.service'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { ProductMoveService } from '../../../services/entity-services/productMove.service'
import { UserService } from '../../../services/entity-services/user.service'
import { User, ProductMovePurpose } from 'depoto-core/src/entities'

const DEFAULT_FILTERS = {
  product: null,
  products: [],
  categories: [],
  checkouts: [],
  depots: [],
  type: '',
  depotFrom: null,
  depotTo: null,
  // createdBy: null,
  createdBy: null,
  created: {
    right_date: '',
    left_date: '',
  },
  expirationDate: '',
  batch: '',
  withoutCheckout: 'all',
  purpose: 'all',
}

@Component({
  selector: 'product-moves-list',
  templateUrl: 'product-moves-list.component.html',
  styleUrls: ['product-moves-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductMovesListComponent extends BaseListing implements AfterViewInit, OnInit {
  private productMovesService = inject(ProductMoveService)
  private tagService = inject(TagService)
  private userService = inject(UserService)
  @ViewChildren(PopoverDirective) popovers: QueryList<PopoverDirective>
  @ViewChild('filterForm') filter_form: FilterFormComponent
  operationTypes: string[] = ['all', 'in', 'out', 'transfer', 'reservation']
  selectedOperationType = 'all'
  selectedDepotFrom = 0
  selectedDepotTo = 0
  filterFrom: string
  filterTo: string
  filterUser = 0
  batches: string[] = ['all']
  selectedBatch = 'all'
  expirationDates: string[] = ['all']
  totalItems = computed(() => this.queryProductMoves.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  filtersExpanded = signal(false)

  isOutSelected = computed(() => this.filters().type === 'out')
  isInSelected = computed(() => this.filters().type === 'in')

  currentPage: number
  endPage: number
  sortProp = signal<string>(
    this.core.services.storage.getSync(this.getStorageKeyForEntity('productMoves'))?.sortProp
      ? this.core.services.storage.getSync(this.getStorageKeyForEntity('productMoves'))?.sortProp
      : 'created'
  )

  sortDir = signal<'asc' | 'desc'>(
    this.core.services.storage.getSync(this.getStorageKeyForEntity('productMoves'))?.sortDir
      ? this.core.services.storage.getSync(this.getStorageKeyForEntity('productMoves'))?.sortDir
      : 'desc'
  )
  expirations: string[] = ['all', 'monthEnd', 'after1month', 'after3months', 'custom']
  selectedExpiration = 'all'
  selectedExpirationDate: string
  private readonly expirationPresets: Array<'monthEnd' | 'after1month' | 'after3months'> = [
    'monthEnd',
    'after1month',
    'after3months',
  ]

  filters = signal(this.loadViewSettings('productMoves')?.filters ?? structuredClone(DEFAULT_FILTERS))

  // filters().expirationDate is stored (and sent to the server) as 'YYYY-MM-DD 23:59:59', but a
  // native <input type="date"> only accepts a bare 'YYYY-MM-DD' - passing the full value through
  // makes the browser reject it and render blank. Strip the time portion for display only.
  expirationDateOnly = computed(() => this.filters().expirationDate?.substring(0, 10) ?? '')

  // Keep external API consistent: template binds [filters]="queryFilters"
  // Refactor: derive from component's `filters` signal and normalize component-specific selections
  queryFilters = computed(() => {
    // const base: any = this.filters?.() ?? {}
    // const out: any = { ...(base || {}) }
    const filtersWithValues = { product: this.productDetailService.product?.id }
    for (const [key, value] of Object.entries(this.filters())) {
      // if it has value
      if (
        value !== undefined &&
        value !== null &&
        value !== false &&
        value !== 0 &&
        value !== '' &&
        value !== 'all' &&
        (!Array.isArray(value) || (Array.isArray(value) && value?.length))
      ) {
        if (key === 'type' && !isNaN(+value)) {
          // array
          filtersWithValues[key] = Number(value)
        } else if (key === 'product' || key === 'createdBy' || key === 'depotFrom' || key === 'depotTo') {
          // value can be a raw id (from a plain select) or an object with id; 0 means "all", so omit it
          const id = Number((value as any)?.id ?? value)
          if (id) {
            filtersWithValues[key] = id
          }
        } else if (key === 'created') {
          const left = (value as any)?.left_date
          const right = (value as any)?.right_date
          if ((left && left.length) || (right && right.length)) {
            filtersWithValues[key] = {
              ...(left?.length && {
                // left_date: left + 'T00:00:00',
                left_date: left,
              }),
              ...(right?.length && {
                right_date: withDateBoundary(right, 'end'),
              }),
            }
          }
        } else if (key === 'expirationDate') {
          console.log(key)
          console.log(value)
          // filtersWithValues[key] = value + 'T23:59:59'
          filtersWithValues[key] = value
        } else {
          // other
          filtersWithValues[key] = value
        }
      }
    }

    return filtersWithValues
  })

  private readonly submittedFilters = createSubmittedFilterState<typeof DEFAULT_FILTERS, Record<string, any>>({
    saveView: filters => this.saveSubmittedView(filters),
    refetch: () => this.queryProductMoves.refetch(),
  })
  readonly submittedQueryFilters = computed(() => this.submittedFilters.snapshot()?.request ?? {})

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name', direction: 'asc' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'product_move_pack'),
  }))

  readonly queryUsers = injectQuery(() => ({
    queryKey: ['users', 'options'],
    queryFn: async () => {
      return await this.userService
        .getUsers(
          { page: 1, sort: 'lastName', direction: 'asc' },
          {
            id: null,
            name: null,
          }
        )
        .then(result => {
          return result?.items
        })
    },
  }))

  users = computed(() => {
    const u = (this.queryUsers.data() ?? []) as User[]
    return [new User({ id: 0, name: 'all', email: 'all' }), ...u]
  })

  readonly queryPurposesOptions = injectQuery(() => ({
    queryKey: ['productMovePurposes', 'options'],
    queryFn: async () => {
      return await this.productMovesService
        .getProductMovePurposes({ sort: 'name', direction: 'asc' }, { id: null, name: null })
        .then(r => r.items)
    },
  }))

  purposes = computed(() => {
    const p = (this.queryPurposesOptions.data() ?? []) as ProductMovePurpose[]
    return [new ProductMovePurpose({ id: 'all', name: 'all' } as any), ...p]
  })

  readonly queryProductMoves = injectInfiniteQuery(() => {
    const snapshot = this.submittedFilters.snapshot()
    const sortProp = this.sortProp()
    const sortDir = this.sortDir()
    const requestFilters = snapshot?.request ?? {}

    return {
      enabled: this.submittedFilters.ready(),
      queryKey: ['productMoves', 'list', { sortProp, sortDir, filters: requestFilters }],
      queryFn: ({ pageParam }) =>
        this.productMovesService.getProductMoves(
          {
            page: pageParam,
            sort: sortProp,
            direction: sortDir,
            filters: requestFilters,
          },
          SchemaUtil.productMove
        ),
      initialPageParam: 1,
      getNextPageParam: result => {
        return Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
          ? Number(result?.paginator?.current) + 1
          : undefined
      },
    }
  })

  productMoves = computed(() => {
    const pages = this.queryProductMoves.data()?.pages ?? []
    return pages.flatMap(p => p?.items ?? [])
  })

  loading = computed(() => this.queryProductMoves.isFetching() || this.queryProductMoves.isPending())

  loadingFilters = computed(
    () => this.queryTags.isFetching() || this.queryUsers.isFetching() || this.queryPurposesOptions.isFetching()
  )

  constructor(
    public core: CoreService,
    public productDetailService: ProductDetailService
  ) {
    super(core)
    effect(() => {
      this.queryProductMoves.data()
      this.queryTags.data()
      this.queryUsers.data()
      this.queryPurposesOptions.data()
    })
    // depotFrom is irrelevant for 'in' moves, depotTo is irrelevant for 'out' moves - clear stale values
    effect(() => {
      const type = this.filters().type
      if (type === 'in' && this.filters().depotFrom) {
        this.setFilter('depotFrom', null)
      } else if (type === 'out' && this.filters().depotTo) {
        this.setFilter('depotTo', null)
      }
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('productMoves')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.selectedOperationType = view?.selectedOperationType || 'all'
    this.selectedDepotFrom = view?.selectedDepotFrom || 'all'
    this.selectedDepotTo = view?.selectedDepotTo || 'all'
    this.sortProp.set(view?.sortProp || 'created')
    this.sortDir.set(view?.sortDir || 'desc')
    this.restoreSelectedExpirationFromFilters()

    this.loadFiltersFromStock()
    this.submittedFilters.commit(this.filters(), this.queryFilters())
    this.queryProductMoves.refetch()
  }

  public clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.submitFilters()
  }

  public submitFilters(): void {
    this.submittedFilters.submit(this.filters(), this.queryFilters())
  }

  private saveSubmittedView(
    filters = this.submittedFilters.snapshot()?.view ?? structuredClone(DEFAULT_FILTERS),
    sortProp = this.sortProp(),
    sortDir = this.sortDir()
  ): void {
    this.saveViewSettings('productMoves', {
      filters,
      selectedOperationType: this.selectedOperationType,
      selectedDepotFrom: this.selectedDepotFrom,
      selectedDepotTo: this.selectedDepotTo,
      sortProp,
      sortDir,
    })
  }

  loadFiltersFromStock() {
    const batches = []
    const expirationDates = []
    // Read straight off the product's own productDepots rather than productDetailService.stock:
    // stock is grouped by ProductComponent.formatStock() against queryDepotsOptions, a separate
    // query that isn't guaranteed to have resolved yet when formatStock() runs. If it hasn't
    // (e.g. its cache isn't already warm from an earlier page this session), productDepots get
    // dropped from stock and nothing re-runs formatStock()/this method once it does resolve - so
    // batches show up inconsistently depending on load order. product.productDepots comes
    // straight from the product fetch itself, with no such race.
    if (!this.productDetailService.product?.isBundle) {
      ;(this.productDetailService.product?.productDepots ?? []).forEach(productDepot => {
        if (productDepot.batch) {
          batches.push(productDepot.batch)
        }
        if (productDepot.expirationDate) {
          expirationDates.push(productDepot.expirationDate)
        }
      })
    }
    this.batches = ['all', ...Array.from(new Set(batches))]
    this.expirationDates = ['all', ...Array.from(new Set(expirationDates))]
  }

  setExpirationFilter(expStr: string = null, expDate: string = null) {
    if (expStr) {
      this.selectedExpiration = expStr
      switch (this.selectedExpiration) {
        case 'all':
        case 'custom':
          this.setFilter('expirationDate', null)
          return
        case 'monthEnd':
        case 'after1month':
        case 'after3months':
          this.setFilter('expirationDate', this.computePresetExpirationDate(this.selectedExpiration))
          return
      }
    } else if (expDate) {
      this.setFilter('expirationDate', `${new Date(expDate).toISOString().substring(0, 10)} 23:59:59`)
    }
  }

  private computePresetExpirationDate(preset: 'monthEnd' | 'after1month' | 'after3months'): string {
    let date = new Date()
    switch (preset) {
      case 'monthEnd':
        date = new Date(date.getFullYear(), date.getMonth() + 1, 0)
        break
      case 'after1month':
        date.setMonth(date.getMonth() + 1)
        break
      case 'after3months':
        date.setMonth(date.getMonth() + 3)
        break
    }

    const tzOffset = date.getTimezoneOffset()
    date.setTime(date.getTime() + tzOffset * 60 * -1000)

    return `${date.toISOString().substring(0, 10)} 23:59:59`
  }

  // The expiration select's options ('monthEnd', 'after1month', ...) are presets, but what's
  // actually stored in filters (and sent to the server) is the exact date they resolve to. On
  // reload, filters.expirationDate is restored correctly, but selectedExpiration - which drives
  // the select - isn't part of that saved state, so match the remembered date back to whichever
  // preset produces it today (or fall back to 'custom' if it doesn't match any).
  private restoreSelectedExpirationFromFilters(): void {
    const expirationDate = this.filters().expirationDate
    if (!expirationDate) {
      this.selectedExpiration = 'all'
      return
    }
    const preset = this.expirationPresets.find(p => this.computePresetExpirationDate(p) === expirationDate)
    this.selectedExpiration = preset ?? 'custom'
  }

  async switchDepots() {
    const depotFrom: number = this.selectedDepotFrom
    this.selectedDepotFrom = this.selectedDepotTo
    this.selectedDepotTo = depotFrom
    this.queryProductMoves.refetch()
  }

  async sortBy(property: string) {
    if (this.sortProp() === property) {
      if (this.sortDir() === 'asc') this.sortDir.set('desc')
      else this.sortDir.set('asc')
    } else {
      this.sortProp.set(property)
    }
    this.currentPage = 1
    this.saveSubmittedView()
  }

  setFilter(property: string, value: any, dateLeftRight?: string) {
    if (dateLeftRight) {
      this.filters.set({
        ...this.filters(),
        [property]: {
          ...this.filters()[property],
          [dateLeftRight]: value,
        },
      })
    } else {
      this.filters.set({
        ...this.filters(),
        [property]: value,
      })
    }
  }

  ngAfterViewInit() {
    this.popovers.forEach((popover: PopoverDirective) => {
      popover.onShown.subscribe(() => {
        this.popovers.filter(p => p !== popover).forEach(p => p.hide())
      })
    })
  }
}
